/**
 * SkillDeployDialog — pick a version + target, then deploy.
 *
 * Target modes:
 *   • all_endpoints   — fan-out to every endpoint in the tenant, incl.
 *                       ones that register later.
 *   • endpoint_set    — explicit set of endpoint IDs picked via a
 *                       hostname-search + checkbox list backed by
 *                       `GET /v1/endpoints` (same source as
 *                       components/EndpointPicker.jsx). Server-side
 *                       hostname filter; pagination via a Load-more chip.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deploySkill, skillStateLabel } from "../../lib/skillsClient.js";
import { listEndpoints } from "../../lib/reportClient.js";
import { applyEndpointDedupe, endpointIdentityBadge } from "../../lib/endpointDedupe.js";
import Select from "../Select.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { isSurfaceDenied, useMyPermissions } from "../../lib/permissions.js";

const inputCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";
const primaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer";
const secondaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer";

const PAGE_SIZE = 50;

export default function SkillDeployDialog({ skill, versions, onClose, onDeployed }) {
    const caps = useMyPermissions();
    const endpointPickerDenied = isSurfaceDenied(caps, "ListEndpoints");
    // Default to the latest published version, falling back to the first.
    const versionOptions = useMemo(
        () => (versions ?? []).map((v) => ({
            value: v.id,
            label: `v${v.versionNumber ?? v.version_number ?? "?"} · ${skillStateLabel(v.state)}`,
        })),
        [versions],
    );
    const defaultVersionId = useMemo(() => {
        if (!versions?.length) return "";
        const pub = [...versions]
            .reverse()
            .find((v) => v.state === "SKILL_VERSION_STATE_PUBLISHED");
        return (pub ?? versions[0]).id;
    }, [versions]);
    const [versionId, setVersionId] = useState(defaultVersionId);
    useEffect(() => { setVersionId(defaultVersionId); }, [defaultVersionId]);

    const [targetMode, setTargetMode] = useState("all"); // "all" | "endpoints"
    const [phase, setPhase] = useState("idle");
    const [error, setError] = useState(null);

    // ── Endpoint picker state ────────────────────────────────────
    const [search, setSearch] = useState("");
    const [endpoints, setEndpoints] = useState([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(null);
    const [endpointsLoading, setEndpointsLoading] = useState(false);
    const [endpointsError, setEndpointsError] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const visibleEndpoints = useMemo(() => applyEndpointDedupe(endpoints), [endpoints]);

    const reqRef = useRef(0);

    const fetchPage = useCallback(async ({ reset = false, query = "", pageToLoad = 0 } = {}) => {
        const reqId = ++reqRef.current;
        setEndpointsLoading(true);
        setEndpointsError(null);
        try {
            const resp = await listEndpoints({
                page: pageToLoad,
                pageSize: PAGE_SIZE,
                hostname: query || undefined,
            });
            if (reqRef.current !== reqId) return; // raced
            const next = Array.isArray(resp?.endpoints) ? resp.endpoints : [];
            setEndpoints((cur) => (reset ? next : [...cur, ...next]));
            setTotal(resp?.total ?? resp?.totalCount ?? null);
            setPage(pageToLoad);
        } catch (err) {
            if (reqRef.current !== reqId) return;
            console.error("[skills] listEndpoints", err);
            setEndpointsError(err.message || String(err));
        } finally {
            if (reqRef.current === reqId) setEndpointsLoading(false);
        }
    }, []);

    // Initial / search-driven fetch. Only fires while the picker is in
    // view (targetMode === "endpoints") so opening the dialog with
    // "All endpoints" selected doesn't burn a list call until the user
    // flips to the picker.
    useEffect(() => {
        if (targetMode !== "endpoints" || endpointPickerDenied) return;
        const handle = setTimeout(() => {
            fetchPage({ reset: true, query: search.trim(), pageToLoad: 0 });
        }, search.trim() ? 200 : 0);
        return () => clearTimeout(handle);
    }, [targetMode, endpointPickerDenied, search, fetchPage]);

    useEffect(() => {
        if (endpointPickerDenied && targetMode === "endpoints") setTargetMode("all");
    }, [endpointPickerDenied, targetMode]);

    const toggle = (id) => setSelectedIds((cur) => {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const toggleAllVisible = () => setSelectedIds((cur) => {
        const next = new Set(cur);
        const allChecked = visibleEndpoints.every((e) => next.has(e.id ?? e.endpointId));
        for (const e of visibleEndpoints) {
            const id = e.id ?? e.endpointId;
            if (!id) continue;
            if (allChecked) next.delete(id); else next.add(id);
        }
        return next;
    });

    const canSubmit =
        !!skill?.id &&
        !!versionId &&
        phase === "idle" &&
        (targetMode === "all" || selectedIds.size > 0);

    const submit = useCallback(async () => {
        if (!canSubmit) return;
        setError(null);
        setPhase("deploying");
        try {
            const target = targetMode === "all"
                ? { kind: "all" }
                : { kind: "endpoints", endpointIds: Array.from(selectedIds) };
            await deploySkill({ skillId: skill.id, versionId, target });
            setPhase("idle");
            onDeployed?.();
            onClose?.();
        } catch (err) {
            console.error("[skills] deploy", err);
            setError(err.message || String(err));
            setPhase("idle");
        }
    }, [canSubmit, skill, versionId, targetMode, selectedIds, onDeployed, onClose]);

    const visibleAllChecked = visibleEndpoints.length > 0 &&
        visibleEndpoints.every((e) => selectedIds.has(e.id ?? e.endpointId));

    return (
        <div className="grid gap-[14px]">
            <div className="grid gap-[4px]">
                <span className={NOTE_CLASS}>Skill</span>
                <span className="text-[12px] text-[color:var(--ink)]">{skill?.name ?? "—"}</span>
            </div>

            <div className="grid gap-[4px]">
                <span className={NOTE_CLASS}>Version</span>
                {versionOptions.length ? (
                    <Select
                        ariaLabel="Version"
                        value={versionId}
                        onChange={setVersionId}
                        options={versionOptions}
                    />
                ) : (
                    <span className={`${NOTE_CLASS} text-[11px]`}>No versions available.</span>
                )}
            </div>

            <fieldset className="grid gap-[6px]">
                <span className={NOTE_CLASS}>Target</span>
                <label className="flex items-center gap-[6px] text-[12px] text-[color:var(--ink)]">
                    <input
                        type="radio"
                        name="skill-deploy-target"
                        checked={targetMode === "all"}
                        onChange={() => setTargetMode("all")}
                    />
                    All current and future endpoints
                </label>
                {!endpointPickerDenied ? (
                    <label className="flex items-center gap-[6px] text-[12px] text-[color:var(--ink)]">
                        <input
                            type="radio"
                            name="skill-deploy-target"
                            checked={targetMode === "endpoints"}
                            onChange={() => setTargetMode("endpoints")}
                        />
                        Specific endpoints
                    </label>
                ) : (
                    <span className={`${NOTE_CLASS} text-[11px]`}>Specific-endpoint targeting is unavailable for a subject-scoped account.</span>
                )}

                {targetMode === "endpoints" ? (
                    <div className="grid gap-[8px] pt-[2px]">
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by hostname…"
                            className={inputCls}
                        />
                        <div className="flex items-center justify-between gap-[8px] text-[10px]">
                            <span className={NOTE_CLASS}>
                                {selectedIds.size} selected
                                {total != null ? ` · ${endpoints.length} of ${total} loaded` : ""}
                            </span>
                            {endpoints.length ? (
                                <button
                                    type="button"
                                    onClick={toggleAllVisible}
                                    className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                                >
                                    {visibleAllChecked ? "Unselect this page" : "Select this page"}
                                </button>
                            ) : null}
                        </div>
                        <div className="max-h-[260px] overflow-y-auto border border-[color:var(--line)]">
                            {endpoints.length === 0 && endpointsLoading ? (
                                <div className={`${NOTE_CLASS} text-[11px] px-[10px] py-[8px]`}>Loading…</div>
                            ) : endpoints.length === 0 ? (
                                <div className={`${NOTE_CLASS} text-[11px] px-[10px] py-[8px]`}>
                                    {endpointsError ? (
                                        <span className="text-red-400" title={endpointsError}>Failed to load</span>
                                    ) : (
                                        "No endpoints match."
                                    )}
                                </div>
                            ) : (
                                <ul className="grid gap-[1px]">
                                    {visibleEndpoints.map((e) => {
                                        const id = e.id ?? e.endpointId;
                                        const hostname = e.hostname ?? e.host_name ?? id;
                                        const checked = selectedIds.has(id);
                                        const identityBadge = endpointIdentityBadge(e);
                                        return (
                                            <li key={id} className="flex items-center gap-[8px] px-[10px] py-[5px] text-[12px] hover:bg-[color:var(--control-bg)] cursor-pointer" onClick={() => toggle(id)}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggle(id)}
                                                    onClick={(ev) => ev.stopPropagation()}
                                                />
                                                <span className="min-w-0 truncate text-[color:var(--ink)]">{hostname}</span>
                                                {identityBadge ? (
                                                    <span
                                                        title={identityBadge.title}
                                                        className="shrink-0 rounded-[2px] border border-[color:var(--line-strong)] px-[4px] py-[1px] text-[8px] uppercase tracking-[0.06em] text-[color:var(--muted)]"
                                                    >
                                                        {identityBadge.label}
                                                    </span>
                                                ) : null}
                                                <span className="ml-auto font-mono text-[10px] text-[color:var(--muted-soft)] truncate">{id}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                        {total != null && endpoints.length < total ? (
                            <div className="grid place-items-center">
                                <button
                                    type="button"
                                    disabled={endpointsLoading}
                                    onClick={() => fetchPage({ reset: false, query: search.trim(), pageToLoad: page + 1 })}
                                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                                >
                                    Load more
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </fieldset>

            {error ? <span className="text-[11px] text-red-400">{error}</span> : null}

            <div className="flex items-center justify-end gap-[6px]">
                <button type="button" onClick={onClose} disabled={phase !== "idle"} className={secondaryBtnCls}>
                    Cancel
                </button>
                <button type="button" onClick={submit} disabled={!canSubmit} className={primaryBtnCls}>
                    {phase === "deploying" ? "Deploying…" : "Deploy"}
                </button>
            </div>
        </div>
    );
}
